import React, { useEffect, useState, useRef } from 'react';
import { PropTypes } from 'prop-types';
import { IconButton, OutlinedInput } from '@mui/material';
import moment from 'moment';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { commaEvery3rdChar } from '../../../utils';
import { ReactComponent as BlueCalendar } from '../../../images/icons/blue_calendar.svg';

import './HoverInput.scss';

const DATE_PARSE_FORMATS = ['YYYY-MM-DD', 'MM-DD-YYYY', 'MM/DD/YYYY', 'MM/DD/YY', 'MM-DD-YY'];

function parseMomentDate(raw) {
  if (!raw) return null;
  const parsed = moment(raw, DATE_PARSE_FORMATS, true);
  return parsed.isValid() ? parsed : null;
}

function formatDisplayDate(raw) {
  const parsed = parseMomentDate(raw);
  return parsed ? parsed.format('MM-DD-YYYY') : '';
}

export default function HoverInput({
  setInputValues,
  inputValues,
  label,
  setUpdateHoverInputs,
  dbKey,
  add$,
  addCommas,
  isDate,
}) {
  const [labelHover, setLabelHover] = useState(false);
  const [isEditActive, setEditActive] = useState(false);
  const [value, setValue] = useState('');
  const [error, setError] = useState(false);
  const prevValueRef = useRef('');

  const handleDateChange = (val) => {
    const formatted = val && val.isValid() ? val.format('YYYY-MM-DD') : '';
    setInputValues({ ...inputValues, [dbKey]: formatted });
    setUpdateHoverInputs(true);
  };

  const handleBlur = () => {
    if (add$ || addCommas) {
      setInputValues(value);
      setEditActive(false);
      setError(false);
      setUpdateHoverInputs(true);
    } else {
      setValue(prevValueRef.current);
      setInputValues(prevValueRef.current);
      setUpdateHoverInputs(false);
      setEditActive(false);
    }
  };

  const valueToDisplay = (val) => {
    let v = val;
    if (val && add$) v = `$${commaEvery3rdChar(v)}`;
    if (val && addCommas && !add$) v = commaEvery3rdChar(v);
    setValue(v || '');
    setInputValues(v);
  };

  useEffect(() => {
    if (!isDate && inputValues && typeof inputValues === 'string') {
      valueToDisplay(inputValues);
    }
  }, [inputValues]);

  if (isDate) {
    const displayDate = formatDisplayDate(inputValues?.[dbKey]);
    return (
      <div className="hover-item date-field">
        <div
          className="hover-label"
          onMouseOver={() => { if (!labelHover) setLabelHover(true); }}
          onMouseLeave={() => { if (!isEditActive) setLabelHover(false); }}
          onFocus={() => setLabelHover(true)}
        >
          <span>
            <span className="label-text">
              {label}
              :
            </span>
            {isEditActive ? (
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  className="hover-date-picker"
                  format="MM-DD-YYYY"
                  value={parseMomentDate(inputValues?.[dbKey])}
                  onChange={(val) => {
                    handleDateChange(val);
                    setEditActive(false);
                    setLabelHover(false);
                  }}
                  open
                  onClose={() => {
                    setEditActive(false);
                    setLabelHover(false);
                  }}
                  slots={{ openPickerIcon: BlueCalendar }}
                  slotProps={{
                    textField: {
                      variant: 'outlined',
                      size: 'small',
                      autoFocus: true,
                      placeholder: 'MM-DD-YYYY',
                    },
                  }}
                />
              </LocalizationProvider>
            ) : (
              <>
                <span
                  className="input-value"
                  onClick={() => setEditActive(true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') setEditActive(true);
                  }}
                  role="button"
                  tabIndex={0}
                >
                  {displayDate}
                </span>
                <div className="edit-btn-wrapper">
                  <IconButton
                    className="edit-icon-btn"
                    onClick={() => setEditActive(true)}
                  >
                    <EditIcon style={{ display: labelHover ? 'block' : 'none' }} />
                  </IconButton>
                </div>
              </>
            )}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="hover-item">
      <div
        className="hover-label"
        onMouseOver={() => { if (!labelHover) setLabelHover(true); }}
        onMouseLeave={() => setLabelHover(false)}
        onFocus={() => setLabelHover(true)}
      >
        <span>
          <span className="label-text">
            {label}
            :
          </span>
          {isEditActive ? (
            <span className="hover-input">
              <OutlinedInput
                type="text"
                value={value}
                onChange={(e) => valueToDisplay(e.target.value)}
                onFocus={() => setError(false)}
                onBlur={handleBlur}
                inputProps={{ maxLength: 10 }}
                autoFocus
                onFocusCapture={(e) => e.target.select(e.target.value.length)}
                error={error}
                onKeyDown={(e) => (e.key === 'Enter' && handleBlur(e.target.value))}
              />
              <IconButton
                size="small"
                onClick={() => {
                  setValue(prevValueRef.current);
                  setInputValues(prevValueRef.current);
                  setEditActive(false);
                  setError(false);
                  setUpdateHoverInputs(false);
                }}
              >
                <CloseIcon />
              </IconButton>
            </span>
          ) : (
            <>
              <span
                className="input-value"
                onFocus={() => setLabelHover(true)}
                onClick={() => {
                  prevValueRef.current = value;
                  setEditActive(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    prevValueRef.current = value;
                    setEditActive(true);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                {value || ''}
              </span>
              <div className="edit-btn-wrapper">
                <IconButton
                  className="edit-icon-btn"
                  onFocus={() => setLabelHover(true)}
                  onKeyDown={(e) => e.key === 'Tab' && setLabelHover(false)}
                  onClick={() => {
                    prevValueRef.current = value;
                    setEditActive(true);
                  }}
                >
                  <EditIcon style={{ display: labelHover ? 'block' : 'none' }} />
                </IconButton>
              </div>
            </>
          )}
        </span>
      </div>
    </div>
  );
}

HoverInput.propTypes = {
  setInputValues: PropTypes.func.isRequired,
  inputValues: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]),
  label: PropTypes.string.isRequired,
  setUpdateHoverInputs: PropTypes.func.isRequired,
  dbKey: PropTypes.string,
  add$: PropTypes.bool,
  addCommas: PropTypes.bool,
  isDate: PropTypes.bool,
};

HoverInput.defaultProps = {
  inputValues: '' || {},
  dbKey: '',
  add$: false,
  addCommas: false,
  isDate: false,
};
